import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './About.css';
import AnimatedSectionTitle from './AnimatedSectionTitle';
import F1Tile from './F1Tile';
import MangaTile from './MangaTile';
import AnimeTile from './AnimeTile';
import TravelMap from './TravelMap';
import MusicPlayer from './MusicPlayer';
import YouTubeTile from './YouTubeTile';
import VibeCodeBanner from './VibeCodeBanner';
import { Search } from 'lucide-react';

// Photography metadata interface
interface PhotoMetadata {
  src: string;
  camera?: string;
  cameraImage?: string;
  lens?: string;
  iso?: string;
  aperture?: string;
  shutter?: string;
  focalLength?: string;
  location?: string;
}

const About: React.FC = () => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoMetadata | null>(null);
  const [isHeroImageLoaded, setIsHeroImageLoaded] = useState(false);

  // Photography gallery images with metadata

  const photographs: PhotoMetadata[] = [
    {
      src: '/content/photographs/DSC00238.jpg',
      camera: 'Sony DSLR-A200',
      cameraImage: '/content/cameras/sony-a200.png',
      iso: '100',
      aperture: 'f/1.0',
      shutter: '1/1000'
    },
    {
      src: '/content/photographs/DSC00291.jpg',
      camera: 'Sony DSLR-A200',
      cameraImage: '/content/cameras/sony-a200.png',
      lens: '50mm F1.7',
      iso: '800',
      aperture: 'f/5.6',
      shutter: '1/100',
      focalLength: '50mm'
    },
    {
      src: '/content/photographs/DSC00304.jpg',
      camera: 'Sony DSLR-A200',
      cameraImage: '/content/cameras/sony-a200.png',
      iso: '800',
      aperture: 'f/1.0',
      shutter: '1/30'
    },
    {
      src: '/content/photographs/DSC00619.jpg',
      camera: 'Sony DSLR-A200',
      cameraImage: '/content/cameras/sony-a200.png',
      iso: '800',
      aperture: 'f/1.0',
      shutter: '1/80'
    },
    {
      src: '/content/photographs/DSCF4823.JPG',
      camera: 'Fujifilm X-E3',
      cameraImage: '/content/cameras/fujifilm-xe3.png',
      iso: '160',
      aperture: 'f/2.8',
      shutter: '1/250'
    },
    {
      src: '/content/photographs/DSCF4850.JPG',
      camera: 'Fujifilm X-E3',
      cameraImage: '/content/cameras/fujifilm-xe3.png',
      iso: '160',
      aperture: 'f/2.8',
      shutter: '1/250'
    },
    {
      src: '/content/photographs/DSCF4865.JPG',
      camera: 'Fujifilm X-E3',
      cameraImage: '/content/cameras/fujifilm-xe3.png',
      iso: '160',
      aperture: 'f/2.8',
      shutter: '1/500'
    },
    {
      src: '/content/photographs/DSCF5045.JPG',
      camera: 'Fujifilm X-E3',
      cameraImage: '/content/cameras/fujifilm-xe3.png',
      iso: '160',
      aperture: 'f/4.0',
      shutter: '1/320'
    },
    {
      src: '/content/photographs/DSCF5055.JPG',
      camera: 'Fujifilm X-E3',
      cameraImage: '/content/cameras/fujifilm-xe3.png',
      iso: '160',
      aperture: 'f/4.0',
      shutter: '1/250'
    },
    {
      src: '/content/photographs/DSCF5133.JPG',
      camera: 'Fujifilm X-E3',
      cameraImage: '/content/cameras/fujifilm-xe3.png',
      iso: '320',
      aperture: 'f/2.8',
      shutter: '1/125'
    },
    {
      src: '/content/photographs/IMG_2877-2.jpg',
      camera: 'Canon EOS 7D',
      cameraImage: '/content/cameras/canon-eos7d.png',
      lens: 'EF50mm f/1.8 STM',
      iso: '125',
      aperture: 'f/1.8',
      shutter: '1/320',
      focalLength: '50mm'
    },
    {
      src: '/content/photographs/IMG_2889-2.jpg',
      camera: 'Canon EOS 7D',
      cameraImage: '/content/cameras/canon-eos7d.png',
      lens: 'EF50mm f/1.8 STM',
      iso: '100',
      aperture: 'f/1.8',
      shutter: '1/320',
      focalLength: '50mm'
    },
    {
      src: '/content/photographs/IMG_2936.jpg',
      camera: 'Canon EOS 7D',
      cameraImage: '/content/cameras/canon-eos7d.png',
      lens: 'EF50mm f/1.8 STM',
      iso: '125',
      aperture: 'f/2.0',
      shutter: '1/60',
      focalLength: '50mm'
    },
    {
      src: '/content/photographs/_DSC1284.jpg',
      camera: 'Sony DSLR-A200',
      cameraImage: '/content/cameras/sony-a200.png',
      iso: '100',
      aperture: 'f/1.0',
      shutter: '1/2000'
    },
    {
      src: '/content/photographs/_DSC1375.jpg',
      camera: 'Sony DSLR-A200',
      cameraImage: '/content/cameras/sony-a200.png',
      iso: '100',
      aperture: 'f/1.0',
      shutter: '1/2000'
    },
  ];


  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsMapVisible(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (mapContainerRef.current) {
      observer.observe(mapContainerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Handle keyboard events for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedPhoto) {
        setSelectedPhoto(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhoto]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (selectedPhoto) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedPhoto]);




  return (
    <section className="about-section" id="about">
      <AnimatedSectionTitle title="About Me" className="about-title" />

      {/* Split Layout */}
      <div className="about-split-layout">
        {/* Left Side - Photo */}
        <div className="about-photo-side">
          <div className={`about-photo-wrapper ${isHeroImageLoaded ? 'loaded' : ''}`}>
            <img
              src="/content/About/about main.jpg"
              alt="Aditya Naik"
              className="about-photo"
              onLoad={() => setIsHeroImageLoaded(true)}
            />
          </div>
        </div>

        {/* Right Side - Info */}
        <div className="about-info-side">
          <div className="about-info-content">
            <h2 className="about-name">Aditya Naik</h2>
            <p className="about-role">Product Designer & HCI Graduate Student</p>
            <p className="about-pronunciation">
              pronounced as <strong>aah-dit-yuh</strong><br />
              <span className="about-nickname">people also call me Adi (aah-di)</span>
            </p>
            <p className="about-bio">
              Welcome to my little corner of the internet! I'm an HCI grad student at Indiana University. Outside of design and research, you'll find me hunting for empty equipment at the gym, redeeming Panda Express entrées, and listening to wacky synth progressions.
            </p>

            {/* Map Section */}
            <div className="about-map-section">
              <p className="about-map-text">I come from a small yet a beautiful coastal state called Goa 🌴</p>
              <div ref={mapContainerRef} className={`about-map-container ${isMapVisible ? 'map-visible' : ''}`}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d988276.0566218346!2d73.31909!3d15.2993!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bbfba106336b741%3A0xeaf887ff62f34092!2sGoa!5e0!3m2!1sen!2sin!4v1635000000000!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Map of Goa, India"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* My Story Section */}
      <div className="my-story-section">
        <AnimatedSectionTitle title="My Story" className="my-story-title" />

        {/* First Row: Video Left, Text Right */}
        <div className="story-row">
          <div className="story-slideshow-card">
            <video
              className="story-video"
              autoPlay
              loop
              muted
              playsInline
              onTimeUpdate={(e) => {
                const video = e.currentTarget;
                // Restart video 1 second before the end to trim the last second
                if (video.duration - video.currentTime < 1) {
                  video.currentTime = 0;
                }
              }}
            >
              <source src="/content/About/IMG_7676.MOV" type="video/quicktime" />
              <source src="/content/About/IMG_7676.MOV" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <div className="story-card">
            <p>
              Growing up, I was that kid who would take things apart just to see what was inside. New RC car? Give it a day, and I'd have it completely disassembled on the floor. Why? Because I wondered how it worked. My family affectionately called me their 'तोड़ फोड़ स्पेशलिस्ट' (tod fod specialist, or 'break-and-fix expert'). I enrolled in Information Technology engineering and genuinely enjoyed coding. But somewhere between writing functions and debugging loops, something shifted. My focus moved from perfecting the code to perfecting the <em>experience</em>. The interface, the usability, how people actually felt using what I built. That's where my heart was. I'd stumbled into my calling without even realizing I was looking for it.
            </p>
          </div>
        </div>

        {/* Second Row: Text Left, Slideshow Right */}
        <div className="story-row story-row-reverse">
          <div className="story-card">
            <p>
              Here's what I've figured out: the best design work happens when you genuinely care about the person that's going to be on the other side of the design, the user. Not in some abstract "user-centered" way, but actually caring. Like when I'm mentoring designers, I remember how lost I felt starting out. Here's the thing though: our minds are shaped by different cultures, experiences, and ways of thinking. Even when you try, you can't completely take yourself out of the thinking process. There will be assumptions. But I try my best to catch them, question them, and design around them, knowing that what makes sense to me might not make sense to everyone. It's less about grand gestures and more about noticing the small stuff that actually matters.
            </p>
          </div>
          <div className="story-slideshow-card">
            <img
              src="/content/About/story.jpg"
              alt="Story"
              className="story-image"
              loading="lazy"
            />
          </div>
        </div>
      </div>

      {/* My Fragments Section */}
      <div className="fragments-section">
        <AnimatedSectionTitle title="My Fragments" className="fragments-title" />
        <p className="fragments-tagline">Little things that make me happy :)</p>
        <div className="bento-grid">

        {/* F1 Widget - Top Left */}
        <F1Tile />

        {/* Music Player Widget - Top Right */}
        <MusicPlayer />

        {/* Photography Gallery - Large */}
        <div className="bento-item bento-photography">
          <div className="widget-content photography-widget">
            <div className="photography-header-section">
              <h3 className="photography-title">Through My Lens</h3>
              <p className="photography-caption">Capturing moments that tell stories</p>
            </div>
            <div className="photo-grid">
              {photographs.map((photo, index) => (
                <div
                  key={index}
                  className="photo-item"
                  onClick={() => setSelectedPhoto(photo)}
                >
                  <img
                    src={photo.src}
                    alt={`Photography ${index + 1}`}
                    className="photo-image"
                    loading="lazy"
                  />
                  <div className="photo-overlay">
                    <Search className="photo-zoom-icon" size={32} strokeWidth={2} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Lightbox Modal with Metadata Card - Rendered via Portal */}
        {selectedPhoto && ReactDOM.createPortal(
          <div className="lightbox" onClick={() => setSelectedPhoto(null)}>
            <div className="lightbox-layout" onClick={(e) => e.stopPropagation()}>
              <button className="lightbox-close" onClick={() => setSelectedPhoto(null)}>
                ✕
              </button>

              {/* Image Container */}
              <div className="lightbox-image-container">
                <img src={selectedPhoto.src} alt="Selected photograph" className="lightbox-image" />
              </div>

              {/* Metadata Card */}
              <div className="lightbox-metadata-card">
                <h3 className="metadata-title">Image Details</h3>

                {selectedPhoto.camera && (
                  <div className="metadata-camera-section">
                    <span className="metadata-label">Camera</span>
                    <span className="metadata-value">{selectedPhoto.camera}</span>
                    {selectedPhoto.cameraImage && (
                      <img
                        src={selectedPhoto.cameraImage}
                        alt={selectedPhoto.camera}
                        className="metadata-camera-image"
                      />
                    )}
                  </div>
                )}

                {selectedPhoto.lens && (
                  <div className="metadata-row">
                    <span className="metadata-label">Lens</span>
                    <span className="metadata-value">{selectedPhoto.lens}</span>
                  </div>
                )}

                {selectedPhoto.iso && (
                  <div className="metadata-row">
                    <span className="metadata-label">ISO</span>
                    <span className="metadata-value">{selectedPhoto.iso}</span>
                  </div>
                )}

                {selectedPhoto.aperture && (
                  <div className="metadata-row">
                    <span className="metadata-label">Aperture</span>
                    <span className="metadata-value">{selectedPhoto.aperture}</span>
                  </div>
                )}

                {selectedPhoto.shutter && (
                  <div className="metadata-row">
                    <span className="metadata-label">Shutter Speed</span>
                    <span className="metadata-value">{selectedPhoto.shutter}</span>
                  </div>
                )}

                {selectedPhoto.focalLength && (
                  <div className="metadata-row">
                    <span className="metadata-label">Focal Length</span>
                    <span className="metadata-value">{selectedPhoto.focalLength}</span>
                  </div>
                )}

                {selectedPhoto.location && (
                  <div className="metadata-row">
                    <span className="metadata-label">Location</span>
                    <span className="metadata-value">{selectedPhoto.location}</span>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Manga Widget */}
        <MangaTile />

        {/* Anime Widget */}
        <AnimeTile />

        {/* Travel Map Widget */}
        <TravelMap />

        {/* YouTube Channels Widget */}
        <YouTubeTile />

        {/* Vibe Code Banner */}
        <VibeCodeBanner />
        </div>
      </div>
    </section>
  );
};

export default About;
